import { useState } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router'
import { Image } from 'theme-ui'
import Logomark from '../framework/Logomark'

const FONT = "'Inter Tight', -apple-system, BlinkMacSystemFont, sans-serif"
const BLUE = '#1056A9'
const INK = '#0F1722'

function LangToggle({ lang, setLang }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        padding: 3,
        borderRadius: 30,
        background: 'rgba(16,86,169,0.07)',
        border: '1px solid rgba(16,86,169,0.14)',
        fontFamily: FONT,
      }}>
      {[
        ['ua', 'УКР'],
        ['en', 'ENG'],
      ].map(([l, lbl]) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          style={{
            border: 'none',
            cursor: 'pointer',
            fontFamily: FONT,
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: '0.04em',
            padding: '6px 13px',
            borderRadius: 24,
            color: lang === l ? '#fff' : BLUE,
            background: lang === l ? BLUE : 'transparent',
            transition: 'all .15s',
          }}>
          {lbl}
        </button>
      ))}
    </div>
  )
}

LangToggle.propTypes = { lang: PropTypes.string, setLang: PropTypes.func }

const STRINGS = {
  ua: {
    heading:
      'Polis — відкрита платформа, що допомагає містам, регіонам і країнам знаходити спільну мову у складних питаннях.',
    intro1:
      'Запущена у 2012 році, платформа пройшла перевірку в десятках тисяч обговорень за участі понад десяти мільйонів учасників з усього світу. Збираючи та аналізуючи погляди тисяч учасників, Polis виявляє точки консенсусу навіть у найгостріших темах.',
    intro2:
      'Polis стала частиною національної демократичної інфраструктури Тайваню, Великої Британії та Фінляндії. Тайвань використовував її для законодавства щодо регулювання Uber, помсти порно та онлайн-продажу алкоголю; Велика Британія — для консультацій з питань національної безпеки; а сервісні округи Фінляндії — регіональні органи, відповідальні за охорону здоров\'я та соціальні послуги, — застосовують Polis для проєктування програм підтримки літніх людей та дитячої психічної допомоги. Уряди Сінгапуру та Філіппін також взяли платформу на озброєння, а в Австрії Klimarat (Національні громадянські збори з питань клімату) використав Polis для об\'єднання тисяч громадян та експертів.',
    intro3:
      'На місцевому рівні Амстердам, Боулінг-Грін (Кентуккі) та кілька британських міст використали Polis для покращення життя мешканців. ПРООН розгорнув її для того, що назвав «найбільшими онлайн-дорадчими вправами в історії», залучивши 30 000 молодих людей у Бутані, Східному Тиморі та Пакистані.',
    intro4:
      'Polis розроблена та підтримується Computational Democracy Project (CompDem), некомерційною організацією зі США. Про платформу писали MIT Technology Review, Wired, The Economist і The New York Times, вона фігурувала в документальних фільмах BBC та PBS.',
    casesLink: 'Кейси Polis з усього світу →',

    polis2Heading: 'Polis 2.0',
    polis2Intro:
      'CompDem представляє Polis 2.0 — вдосконалену версію оригінальної платформи. Система поєднує масштаб мільйонів одночасних учасників з автоматичним картуванням сотень тисяч поглядів, LLM-звітами в реальному часі та можливістю вести обговорення необмежено довго.',
    polis2Sub: 'Polis 2.0 досягає цього масштабу завдяки чотирьом ключовим механізмам:',
    mechanisms: [
      {
        t: 'Хмарна масштабованість',
        d: 'Розподілена хмарна інфраструктура масштабується в реальному часі відповідно до попиту. Якщо найбільше розгортання Polis 1.0 налічувало 33 547 учасників (обговорення партії Aufstehen у Німеччині), то Polis 2.0 здатна підтримувати мільйони одночасних учасників.',
      },
      {
        t: 'Динамічне картування думок',
        d: 'Polis групує учасників за схожістю голосів і поданих тверджень. Спираючись на десятиліття надійних статистичних алгоритмів, Polis 2.0 застосовує більш точні підходи. Групування оновлюються в реальному часі в міру розвитку обговорення.',
      },
      {
        t: 'Семантична кластеризація',
        d: 'Polis 2.0 — перша система, що використовує бібліотеку EVōC (Tutte Institute) для автоматичної організації обговорень у теми та підтеми. Учасники можуть обирати теми, що їх цікавлять, і таким чином колективно формувати порядок денний.',
      },
      {
        t: 'Наскрізна автоматизація',
        d: 'Polis 2.0 автоматизує наповнення, модерацію (включаючи фільтрацію токсичності), семантичну кластеризацію та генерацію звітів. Це усуває необхідність у фасилітаторах-експертах зі збереженням можливості людського контролю.',
      },
    ],

    howHeading: 'Як працює Polis 2.0',

    step1Heading: '1. Налаштування обговорення',
    step1p1:
      'Polis «засівається» набором тверджень, за якими учасники можуть голосувати «згоден», «не згоден» або «пропустити».',
    step1p2:
      'Основний формат — короткі твердження (1–3 речення), оптимізовані для мобільного голосування. Більшість генерується безпосередньо учасниками.',
    step1p3: 'Інші формати вхідних даних можна попередньо обробити за допомогою LLM і завантажити через CSV:',
    step1items: [
      'Довгі наративи (розбиті на твердження для голосування)',
      'Транскрипти семінарів (результати живих обговорень)',
      'Публікації в соцмережах (Facebook, Instagram, YouTube тощо)',
      'Коментарі з онлайн-медіа (зібрані та відфільтровані)',
      'Email-повідомлення (текстові надходження від «нецифрових» учасників)',
      'Голосові записи (транскрибовані та оброблені)',
    ],

    step2Heading: '2. Запрошення учасників',
    step2p1: 'Polis 2.0 включає кілька систем для управління ідентичністю та залученням учасників:',
    step2items: [
      {
        t: 'Дерева запрошень',
        d: 'Структурована система запрошень відстежує, як учасники приєднуються до обговорень, забезпечуючи органічне зростання через мережі при збереженні якості.',
      },
      {
        t: 'Управління ідентичністю',
        d: 'Розширені можливості XID (зовнішній ідентифікатор) та OIDC-провайдери автентифікації забезпечують безпечний та гнучкий доступ учасників.',
      },
      {
        t: 'Портативність даних',
        d: 'Повна портативність даних з підтримкою XID дозволяє відстежувати та аналізувати учасників між платформами — сумісно з SurveyMonkey, Qualtrics, Typeform та Google Forms.',
      },
    ],

    step3Heading: '3. Участь у Polis 2.0',
    step3p1: 'Учасники Polis 2.0 можуть:',
    step3items: [
      'Обирати теми, що їх цікавлять — колективно визначаючи порядок денний',
      'Голосувати за твердження інших — згоден, не згоден або пропустити',
      'Подавати власні твердження з питань, важливих для них',
      'Позначати особливо важливі для них твердження (за бажанням)',
    ],
    step3multilingual:
      'Багатомовність: система визначає мову браузера учасника і автоматично перекладає інтерфейс та твердження. Учасники можуть подавати твердження будь-якою мовою.',

    step4Heading: '4. Модерація Polis 2.0',
    step4p1:
      'Обговорення з десятками тисяч тверджень потребують ефективної модерації. Polis 2.0 включає функції на основі ШІ:',
    step4items: [
      { t: 'Виявлення токсичності', d: 'Позначення мови ненависті, переслідувань та екстремістського контенту в реальному часі' },
      { t: 'Обробка мови', d: 'Автоматичний переклад для багатомовної участі' },
    ],
    step4human: 'Людський контроль (рекомендовано):',
    step4humanItems: [
      { t: 'Перевірка', d: 'Людська перевірка рішень ШІ-модерації' },
      { t: 'Культурна чутливість', d: 'Спеціалізована перевірка для внесків маргіналізованих спільнот' },
      { t: 'Фактчекінг експертів', d: 'Спеціалісти перевіряють твердження щодо технічних деталей' },
      { t: 'Стандарти спільноти', d: 'Прозорі правила модерації, розроблені спільно з учасниками' },
    ],
    step4note:
      'Крім того, система маршрутизації тверджень виконує функцію модерації, визначаючи оптимальний порядок показу тверджень кожному учаснику.',

    step5Heading: '5. Аналіз і візуалізація в реальному часі',
    step5outputs: 'Результати Polis 2.0',
    step5topic: {
      t: 'Комплексне картування тем і думок',
      d: 'Polis 2.0 картує обговорення, виявляючи популярні теми, підтеми та їх зв\'язки, зони консенсусу та точки розбіжностей.',
    },
    step5consensus: {
      t: 'Твердження консенсусу',
      d: 'Для кожної теми та підтеми платформа генерує колективні твердження, що відображають згоду між усіма групами, підкріплені коментарями та голосами. Ці твердження представляють автентичний консенсус, а не нав\'язаний компроміс.',
    },
    step5report: {
      t: 'Автоматична генерація звітів',
      d: 'Polis 2.0 генерує наративні звіти та може використовувати кілька LLM-моделей. Звіти охоплюють усе обговорення або фокусуються на конкретних темах. Кожне твердження у звіті містить посилання для легкої верифікації.',
    },
    step5data: {
      t: 'Сховище даних',
      d: 'Усі дані залишаються доступними для подальшого аналізу та використання.',
    },

    navIn: 'Увійти',
    copyright: '© 2026 Center for Innovations Development',
  },
  en: {
    heading:
      'Polis is an open-source platform that helps entire cities, states, or even countries find common ground on complex issues.',
    intro1:
      "First launched in 2012, it's been stress tested in tens of thousands of conversations with more than ten million participants worldwide. By collecting and analyzing viewpoints from thousands of participants, Polis reveals points of consensus, even on topics that seem deadlocked.",
    intro2:
      "Polis has become part of the national democratic infrastructure in Taiwan, the UK, and Finland. Taiwan has used it to craft legislation on issues ranging from Uber regulation to revenge porn and online liquor sales; the UK has employed it for national security consultations; and Finland's wellbeing services counties — regional bodies responsible for health and social services — use Polis to design programs like support for elderly safety and mental health services for children. Governments in Singapore and the Philippines have also adopted the platform, while in Austria, the Klimarat (the National Citizens' Assembly on Climate) used Polis to bring together thousands of citizens and experts to develop climate proposals.",
    intro3:
      "At the local level, Amsterdam, Bowling Green, Kentucky, and multiple UK cities have used Polis to improve residents' lives. The United Nations Development Programme (UNDP) deployed it for what it called \"the largest online deliberative exercises in history,\" engaging 30,000 youth across Bhutan, East Timor, and Pakistan.",
    intro4:
      'Polis is designed, engineered, and maintained by The Computational Democracy Project (CompDem), a U.S.-based 501(c)(3). The tool has been featured in MIT Technology Review, Wired, The Economist, and The New York Times, and in BBC and PBS documentaries.',
    casesLink: 'Polis case studies from around the world →',

    polis2Heading: 'Polis 2.0',
    polis2Intro:
      'CompDem is now introducing Polis 2.0, an enhanced version of the original Polis 1.0 platform. This upgraded system combines massive participation capacity — supporting millions of simultaneous participants — with automated mapping of hundreds of thousands of individual viewpoints, real-time LLM-generated summaries, and the ability to keep conversations open indefinitely.',
    polis2Sub: 'Polis 2.0 achieves this transformative scale through four key mechanisms:',
    mechanisms: [
      {
        t: 'Scalable Cloud Infrastructure',
        d: "Polis 2.0's robust, cloud-powered distributed system scales in real time to meet demand. While Polis 1.0's largest deployment reached 33,547 participants (a conversation hosted by Germany's Aufstehen party), Polis 2.0's infrastructure can support millions of simultaneous participants.",
      },
      {
        t: 'Dynamic Opinion Mapping',
        d: 'Polis groups participants based on how similarly they vote and what statements they submit. Building on a foundation of solid statistical algorithms from a decade ago, Polis 2.0 now employs more refined and nuanced approaches. These groupings update in real-time as the conversation evolves.',
      },
      {
        t: 'Semantic Topic Clustering',
        d: 'Polis 2.0 is the first to use the Embedding Vector Oriented Clustering (EVōC) library from the Tutte Institute to automatically organize conversations into evolving topic hierarchies. Participants can view all topic areas and select those of greatest interest before entering the discussion.',
      },
      {
        t: 'End-to-End Automation',
        d: 'Polis 2.0 automates conversation seeding, moderation (including toxicity filtering), semantic clustering, and report generation. This removes the expert facilitator bottleneck while preserving the option for human oversight.',
      },
    ],

    howHeading: 'How Polis 2.0 works',

    step1Heading: '1. Setting up a Polis 2.0 conversation',
    step1p1: 'Polis is "seeded" with a set of statements that participants can "agree," "disagree," or "pass" on.',
    step1p2:
      'The primary format is short statements (1–3 sentences), optimized for mobile voting — most are generated directly by participants.',
    step1p3: 'Other input formats can be pre-processed using an LLM and entered via CSV:',
    step1items: [
      'Long narratives (chunked into votable statements)',
      'Workshop transcripts (face-to-face discussion outputs)',
      'Social media posts (Facebook, Instagram, YouTube, etc.)',
      'Online media comments (compiled and filtered)',
      'Email submissions (text-based input from non-digital participants)',
      'Voice recordings (transcribed and processed into text)',
    ],

    step2Heading: '2. Inviting Participants',
    step2p1: 'Polis 2.0 includes multiple systems for managing participant identity and growth:',
    step2items: [
      {
        t: 'Invite Trees',
        d: 'A structured invitation system tracks how participants join conversations, enabling organic growth through networks while maintaining quality.',
      },
      {
        t: 'Identity Management',
        d: 'Advanced XID (external identifier) and OIDC authentication providers ensure secure and flexible participant access.',
      },
      {
        t: 'Data Portability',
        d: 'Complete data portability with XID support enables cross-platform participant tracking, compatible with SurveyMonkey, Qualtrics, Typeform, and Google Forms.',
      },
    ],

    step3Heading: '3. Participating on Polis 2.0',
    step3p1: 'On Polis 2.0 participants can:',
    step3items: [
      'Select topics of interest — collectively setting the agenda',
      "Vote on others' statements — agree, disagree, or pass",
      'Submit statements about issues that matter to them',
      'Mark which statements are especially important to them (optional)',
    ],
    step3multilingual:
      "Multi-lingual capabilities: The system detects a participant's browser language and automatically translates the UI text and statements into their preferred language.",

    step4Heading: '4. Moderating Polis 2.0',
    step4p1:
      'Polis conversations with tens of thousands of participant-entered statements require effective moderation. Polis 2.0 includes AI-assisted moderation features:',
    step4items: [
      { t: 'Toxicity Detection', d: 'Real-time flagging of hate speech, harassment, and extremist content' },
      { t: 'Language Processing', d: 'Automatic translation for multilingual participation' },
    ],
    step4human: 'Human Oversight (recommended):',
    step4humanItems: [
      { t: 'Review', d: 'Human review of AI moderation decisions' },
      { t: 'Cultural Sensitivity', d: 'Specialized review for marginalized or under-represented community contributions' },
      { t: 'Expert Fact-checking', d: 'Specialists verify claims about technical details' },
      { t: 'Community Standards', d: 'Transparent moderation guidelines co-developed with participant input' },
    ],
    step4note:
      'In addition, the statement routing system functions as a form of moderation by determining the optimal presentation of statements to each participant.',

    step5Heading: '5. Real-time Analysis & Visualization',
    step5outputs: 'Polis 2.0 Outputs',
    step5topic: {
      t: 'Comprehensive Topic and Opinion Mapping',
      d: 'Polis 2.0 maps the conversation by identifying popular topics, subtopics and their interconnections, areas of consensus, and points of disagreement.',
    },
    step5consensus: {
      t: 'Consensus Statements',
      d: 'For each topic and subtopic, the platform generates collective statements that reflect agreement across all groups, supported by the underlying comments and votes.',
    },
    step5report: {
      t: 'Automated Narrative Report Generation',
      d: 'Polis 2.0 generates automated narrative reports and can draw on multiple LLM models. Each clause in the report includes citations for easy human verification.',
    },
    step5data: {
      t: 'Data Repository',
      d: 'All data remains accessible for ongoing reference and further analysis.',
    },

    navIn: 'Sign in',
    copyright: '© 2026 Center for Innovations Development',
  },
}

function SectionHeading({ children }) {
  return (
    <h2
      style={{
        margin: '0 0 16px',
        fontSize: 28,
        fontWeight: 700,
        color: INK,
        letterSpacing: '-0.02em',
        fontFamily: FONT,
      }}>
      {children}
    </h2>
  )
}
SectionHeading.propTypes = { children: PropTypes.node }

function SubHeading({ children }) {
  return (
    <h3
      style={{
        margin: '0 0 12px',
        fontSize: 20,
        fontWeight: 700,
        color: INK,
        letterSpacing: '-0.01em',
        fontFamily: FONT,
      }}>
      {children}
    </h3>
  )
}
SubHeading.propTypes = { children: PropTypes.node }

function Para({ children, style = {} }) {
  return (
    <p style={{ margin: '0 0 16px', fontSize: 16, lineHeight: 1.65, color: '#5A6883', fontFamily: FONT, ...style }}>
      {children}
    </p>
  )
}
Para.propTypes = { children: PropTypes.node, style: PropTypes.object }

function BulletList({ items }) {
  return (
    <ul style={{ margin: '0 0 24px', paddingLeft: 22, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {items.map((item, i) => (
        <li key={i} style={{ fontSize: 16, lineHeight: 1.6, color: '#5A6883', fontFamily: FONT }}>
          {item}
        </li>
      ))}
    </ul>
  )
}
BulletList.propTypes = { items: PropTypes.arrayOf(PropTypes.node) }

function CardList({ items }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
      {items.map((item, i) => (
        <div
          key={i}
          style={{
            border: '1px solid #E9EDF4',
            borderRadius: 12,
            padding: '16px 20px',
            background: '#fff',
          }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: INK, marginBottom: 4, fontFamily: FONT }}>
            {item.t}
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.55, color: '#5A6883', fontFamily: FONT }}>{item.d}</div>
        </div>
      ))}
    </div>
  )
}
CardList.propTypes = { items: PropTypes.arrayOf(PropTypes.shape({ t: PropTypes.string, d: PropTypes.string })) }

function Divider() {
  return <div style={{ height: 1, background: '#E9EDF4', margin: '40px 0' }} />
}

const Home2 = () => {
  const [lang, setLang] = useState('ua')
  const s = STRINGS[lang]

  return (
    <div style={{ minHeight: '100vh', background: '#fff', fontFamily: FONT }}>

      {/* Header — same as /home */}
      <header
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '24px 64px',
        }}>
        <Link to="/home" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
          <Logomark withText fill="#03A9F4" size={44} />
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 22 }}>
          <LangToggle lang={lang} setLang={setLang} />
          <Link
            to="/signin"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontFamily: FONT,
              fontSize: 14.5,
              fontWeight: 600,
              color: '#1A2A44',
              textDecoration: 'none',
            }}>
            {s.navIn}
          </Link>
        </div>
      </header>

      {/* Content */}
      <main
        style={{
          maxWidth: 800,
          margin: '0 auto',
          padding: '8px 32px 64px',
        }}>

        {/* Introduction */}
        <h1
          style={{
            margin: '24px 0 24px',
            fontSize: 36,
            fontWeight: 700,
            color: INK,
            letterSpacing: '-0.03em',
            lineHeight: 1.15,
            fontFamily: FONT,
          }}>
          {s.heading}
        </h1>
        <Para>{s.intro1}</Para>
        <Para>{s.intro2}</Para>
        <Para>{s.intro3}</Para>
        <Para>{s.intro4}</Para>

        <div style={{ marginBottom: 40 }}>
          <a
            href="https://compdemocracy.org/Case-studies"
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 15, fontWeight: 600, color: BLUE, textDecoration: 'none', fontFamily: FONT }}>
            {s.casesLink}
          </a>
        </div>

        <Divider />

        {/* Polis 2.0 */}
        <SectionHeading>{s.polis2Heading}</SectionHeading>
        <Para>{s.polis2Intro}</Para>
        <Para>{s.polis2Sub}</Para>
        <CardList items={s.mechanisms} />

        <Divider />

        {/* How it works */}
        <SectionHeading>{s.howHeading}</SectionHeading>

        {/* Step 1 */}
        <SubHeading>{s.step1Heading}</SubHeading>
        <Para>{s.step1p1}</Para>
        <Para>{s.step1p2}</Para>
        <Para style={{ marginBottom: 8 }}>{s.step1p3}</Para>
        <BulletList items={s.step1items} />

        {/* Step 2 */}
        <SubHeading>{s.step2Heading}</SubHeading>
        <Para>{s.step2p1}</Para>
        <CardList items={s.step2items} />

        {/* Step 3 */}
        <SubHeading>{s.step3Heading}</SubHeading>
        <Para>{s.step3p1}</Para>
        <BulletList items={s.step3items} />

        <div style={{ margin: '16px 0 24px', textAlign: 'center' }}>
          <Image
            src="/bg2050.png"
            alt="Bowling Green 2050 Conversation"
            sx={{ maxWidth: '100%', height: 'auto', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
          />
        </div>

        <Para>{s.step3multilingual}</Para>

        <div style={{ margin: '16px 0 32px', textAlign: 'center' }}>
          <Image
            src="/bg2050_fr.png"
            alt="Bowling Green 2050 — French"
            sx={{ maxWidth: '100%', height: 'auto', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
          />
        </div>

        {/* Step 4 */}
        <SubHeading>{s.step4Heading}</SubHeading>
        <Para>{s.step4p1}</Para>
        <CardList items={s.step4items} />
        <Para style={{ marginBottom: 8, fontWeight: 600, color: INK }}>{s.step4human}</Para>
        <CardList items={s.step4humanItems} />
        <Para>{s.step4note}</Para>

        {/* Step 5 */}
        <SubHeading>{s.step5Heading}</SubHeading>
        <Para style={{ fontWeight: 600, color: INK, marginBottom: 12 }}>{s.step5outputs}</Para>

        <div
          style={{
            border: '1px solid #E9EDF4',
            borderRadius: 12,
            padding: '16px 20px',
            marginBottom: 16,
            background: '#fff',
          }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: INK, marginBottom: 4, fontFamily: FONT }}>
            {s.step5topic.t}
          </div>
          <Para style={{ marginBottom: 0 }}>{s.step5topic.d}</Para>
        </div>

        <div style={{ margin: '16px 0', textAlign: 'center' }}>
          <Image
            src="/collective.png"
            alt="Collective Statement Panel"
            sx={{ maxWidth: '100%', height: 'auto', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
          />
        </div>

        <div
          style={{
            border: '1px solid #E9EDF4',
            borderRadius: 12,
            padding: '16px 20px',
            marginBottom: 16,
            background: '#fff',
          }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: INK, marginBottom: 4, fontFamily: FONT }}>
            {s.step5consensus.t}
          </div>
          <Para style={{ marginBottom: 0 }}>{s.step5consensus.d}</Para>
        </div>

        <div style={{ margin: '16px 0', textAlign: 'center' }}>
          <Image
            src="/beeswarm.png"
            alt="Topic Stats Beeswarm"
            sx={{ maxWidth: '100%', height: 'auto', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
          />
        </div>

        <div
          style={{
            border: '1px solid #E9EDF4',
            borderRadius: 12,
            padding: '16px 20px',
            marginBottom: 16,
            background: '#fff',
          }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: INK, marginBottom: 4, fontFamily: FONT }}>
            {s.step5report.t}
          </div>
          <Para style={{ marginBottom: 0 }}>{s.step5report.d}</Para>
        </div>

        <div
          style={{
            border: '1px solid #E9EDF4',
            borderRadius: 12,
            padding: '16px 20px',
            marginBottom: 16,
            background: '#fff',
          }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: INK, marginBottom: 4, fontFamily: FONT }}>
            {s.step5data.t}
          </div>
          <Para style={{ marginBottom: 0 }}>{s.step5data.d}</Para>
        </div>

        <div style={{ margin: '16px 0 40px', textAlign: 'center' }}>
          <Image
            src="/export_links.png"
            alt="Data Export Links"
            sx={{ maxWidth: '100%', height: 'auto', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
          />
        </div>

        {/* Interactive topic map */}
        <div
          style={{
            width: '100%',
            height: 500,
            borderRadius: 12,
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            marginBottom: 40,
          }}>
          <iframe
            src="https://polis-delphi.s3.us-east-1.amazonaws.com/visualizations/r3dmevt8dkar2inf2hxei/cd1e07ee-4506-44e9-a91b-ca73054af163/layer_0_datamapplot.html"
            style={{ width: '100%', height: '100%', border: 'none' }}
            title="Interactive Topic Map"
          />
        </div>

        <Divider />

        {/* Footer */}
        <footer
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
            fontSize: 13,
            color: '#8A94A8',
            fontFamily: FONT,
          }}>
          <span>{s.copyright}</span>
          <a
            href="https://github.com/compdemocracy/polis"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: BLUE, textDecoration: 'none', fontWeight: 500 }}>
            GitHub
          </a>
        </footer>
      </main>
    </div>
  )
}

export default Home2
